/**
 * Big bang and join, on a clock.
 *
 * `shared/rings-tween.ts` answers *what* every ring should do to get from one
 * layout to the next — a `RingTransition` per ring, with newcomers starting
 * from where their parent stood and leavers collapsing into where their parent
 * has ended up. This module runs that plan: half a second of ease-in-out cubic,
 * one interpolated transform per ring per frame, and a settle at the end.
 *
 * It knows nothing about the DOM. The scene passes hooks — `onFrame` gets the
 * whole map of interpolated transforms, `onSettle` fires exactly once when the
 * rings have arrived (or immediately, when the reader has asked for less
 * motion). That split is what lets the scene do the cheap thing per frame
 * (write a translate, nudge the marks) and the expensive thing once (re-render
 * every ring, month and day fragment at its final radius, re-pack the floats).
 */
import {
  easeInOutCubic,
  interpolateTransform,
  type RingTransform,
  type RingTransition,
} from '@circle/shared';
import { prefersReducedMotion } from './motion';

/** Half a second, as docs/CIRCLES.md §3 asks for. */
export const RING_TWEEN_MS = 500;

export interface RingTweenHooks {
  /** Where every ring is right now. Called once per frame, and never after `onSettle`. */
  onFrame(at: ReadonlyMap<string, RingTransform>, t: number): void;
  /** The rings have arrived. Fires exactly once per run, cancelled runs included. */
  onSettle(): void;
}

export interface RingTweenerOptions {
  durationMs?: number;
  /** Injectable so tests can drive the clock without a real one. */
  now?: () => number;
  requestFrame?: (callback: () => void) => number;
  cancelFrame?: (handle: number) => void;
  /** A reader who has asked for less motion gets the destination, not the journey. */
  reducedMotion?: () => boolean;
}

function defaultRequestFrame(callback: () => void): number {
  if (typeof requestAnimationFrame === 'function') return requestAnimationFrame(() => callback());
  return setTimeout(callback, 16) as unknown as number;
}

function defaultCancelFrame(handle: number): void {
  if (typeof cancelAnimationFrame === 'function') cancelAnimationFrame(handle);
  else clearTimeout(handle);
}

/** The map a plan looks like at `t`, with `t` already eased. */
export function transformsAt(
  plan: readonly RingTransition[],
  t: number,
): Map<string, RingTransform> {
  const out = new Map<string, RingTransform>();
  for (const step of plan) out.set(step.id, interpolateTransform(step.from, step.to, t));
  return out;
}

export class RingTweener {
  private readonly durationMs: number;
  private readonly now: () => number;
  private readonly requestFrame: (callback: () => void) => number;
  private readonly cancelFrame: (handle: number) => void;
  private readonly reducedMotion: () => boolean;

  private plan: RingTransition[] = [];
  private hooks: RingTweenHooks | undefined;
  private startedAt = 0;
  private handle = 0;

  constructor(options: RingTweenerOptions = {}) {
    this.durationMs = options.durationMs ?? RING_TWEEN_MS;
    this.now = options.now ?? (() => Date.now());
    this.requestFrame = options.requestFrame ?? defaultRequestFrame;
    this.cancelFrame = options.cancelFrame ?? defaultCancelFrame;
    this.reducedMotion = options.reducedMotion ?? prefersReducedMotion;
  }

  get running(): boolean {
    return this.hooks !== undefined;
  }

  /**
   * Start a run, replacing any run already going. A plan with nothing to move —
   * an empty one, or one where every ring is already where it belongs — settles
   * straight away rather than burning half a second on a still image, and so
   * does a run for a reader who prefers reduced motion.
   */
  run(plan: readonly RingTransition[], hooks: RingTweenHooks): void {
    this.stop();
    const moving = plan.filter((step) => !sameTransform(step.from, step.to));
    if (moving.length === 0 || this.durationMs <= 0 || this.reducedMotion()) {
      hooks.onFrame(transformsAt(plan, 1), 1);
      hooks.onSettle();
      return;
    }
    this.plan = [...plan];
    this.hooks = hooks;
    this.startedAt = this.now();
    hooks.onFrame(transformsAt(this.plan, 0), 0);
    this.schedule();
  }

  /** Stop where we are and settle. A run that was not going is left alone. */
  cancel(): void {
    const hooks = this.hooks;
    if (!hooks) return;
    const plan = this.plan;
    this.stop();
    hooks.onFrame(transformsAt(plan, 1), 1);
    hooks.onSettle();
  }

  /** Drop the run without settling — for teardown, where nobody is left to tell. */
  stop(): void {
    if (this.handle) {
      this.cancelFrame(this.handle);
      this.handle = 0;
    }
    this.plan = [];
    this.hooks = undefined;
  }

  private schedule(): void {
    this.handle = this.requestFrame(() => {
      this.handle = 0;
      this.tick();
    });
  }

  private tick(): void {
    const hooks = this.hooks;
    if (!hooks) return;
    const elapsed = this.now() - this.startedAt;
    const raw = Math.min(1, Math.max(0, elapsed / this.durationMs));
    const plan = this.plan;
    hooks.onFrame(transformsAt(plan, easeInOutCubic(raw)), raw);
    if (raw >= 1) {
      this.stop();
      hooks.onSettle();
      return;
    }
    this.schedule();
  }
}

function sameTransform(a: RingTransform, b: RingTransform): boolean {
  return a.cx === b.cx && a.cy === b.cy && a.r === b.r && a.rotation === b.rotation;
}
